import { useEffect, useRef, useState } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { buyBasket, getBaskets, sellBasket } from '../../services/BasketsService';
import './Baskets.css';

function money(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const sign = number > 0 ? '+' : '';
    return `${sign}${number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function qty(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const digits = number >= 1 ? 4 : 8;
    return number.toLocaleString('pt-BR', { maximumFractionDigits: digits });
}

function price(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const digits = number >= 1 ? 2 : 6;
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: digits });
}

function tone(value) {
    const number = Number(value);
    if (!Number.isFinite(number) || number === 0) return 'flat';
    return number > 0 ? 'up' : 'down';
}

function apiError(err, fallback) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data.message === 'string') return data.message;
    return err?.message || fallback;
}

function when(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('pt-BR');
}

function BasketCard({ basket, expanded, busy, usdcFree, onOpen, onClose, onBuy, onSell }) {
    const [confirmSell, setConfirmSell] = useState(false);
    const position = basket.position;
    const riskLevel = basket.risk?.level || 'moderado';

    useEffect(() => {
        if (!position) setConfirmSell(false);
    }, [position]);

    function openCard() {
        if (!expanded) onOpen();
    }

    return (
        <article className={`basket-card ${expanded ? 'is-open' : ''}`} onClick={openCard}>
            <div className="basket-top">
                <div>
                    <p className="basket-kicker">{basket.summary}</p>
                    <h2>{basket.name}</h2>
                </div>
                <div className="basket-perf">
                    <strong className={tone(basket.performancePct)}>{pct(basket.performancePct)}</strong>
                    <span>desempenho em 3 meses</span>
                </div>
            </div>

            <div className="basket-badges">
                <span className={`basket-badge risk-${riskLevel}`}>
                    Risco {basket.risk?.label || 'Moderado'}
                </span>
                <span className="basket-badge neutral">Stop automático −{basket.stopPct || 20}%</span>
                {position ? <span className="basket-badge neutral">Na sua carteira</span> : null}
            </div>

            <ul className="basket-coins">
                {(basket.coins || []).map(coin => (
                    <li key={coin.asset}>
                        <span>{coin.name} <small>{coin.asset}</small></span>
                        <strong className={tone(coin.isCash ? 0 : coin.performancePct)}>
                            {coin.isCash ? 'em dólar' : pct(coin.performancePct)}
                        </strong>
                    </li>
                ))}
            </ul>

            <p className="basket-risk-text">{basket.risk?.text}</p>
            {basket.note ? <p className="basket-note">{basket.note}</p> : null}

                {position ? (
                <p className={`basket-hint ${tone(position.pnlPct)}`}>
                    {position.pnlPct == null
                        ? 'Posição aberta. O resultado aparece quando o preço atualizar.'
                        : `Seu resultado: ${pct(position.pnlPct)} (${money(position.pnlUsdc)} USDC)`}
                </p>
            ) : (
                <p className="basket-hint">
                    {basket.minUsdc > 0 ? `Mínimo ${money(basket.minUsdc)} USDC. ` : ''}
                    Clique no card para investir com o saldo livre.
                </p>
            )}

            {expanded ? (
                <div className="basket-panel" onClick={event => event.stopPropagation()}>
                    <p>{basket.description}</p>
                    {position ? (
                        <>
                            <div className="basket-result">
                                <div>
                                    <span className="basket-meta">Valor agora</span>
                                    <strong>{money(position.valueUsdc)} USDC</strong>
                                </div>
                                <div>
                                    <span className="basket-meta">Investido em {when(position.openedAt)}</span>
                                    <strong className={tone(position.pnlUsdc)}>
                                        {money(position.pnlUsdc)} USDC
                                    </strong>
                                </div>
                            </div>
                            <div>
                                <div className="basket-stop-track">
                                    <span style={{ width: `${Math.min(100, Math.max(0, -(Number(position.pnlPct) || 0)) / (position.stopPct || 20) * 100)}%` }} />
                                </div>
                                <p className="basket-note">
                                    {position.pnlPct == null
                                        ? 'Aguardando o preço para calcular o resultado. O stop de 20% só dispara com o preço atual.'
                                        : Number(position.pnlPct) >= 0
                                            ? `Em lucro. Se cair ${position.stopPct || 20}% do valor investido, todas as moedas são vendidas e o saldo volta para USDC.`
                                            : `Caiu ${pct(Math.abs(position.pnlPct)).replace('+', '')} do valor investido. O stop de ${position.stopPct || 20}% dolariza a cesta inteira.`}
                                </p>
                            </div>
                            <table className="basket-table">
                                <thead>
                                    <tr>
                                        <th>Moeda</th>
                                        <th>Investido</th>
                                        <th>Qtd</th>
                                        <th>Entrada</th>
                                        <th>Agora</th>
                                        <th>Resultado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(position.legs || []).map(leg => (
                                        <tr key={leg.asset}>
                                            <td>{leg.asset}{leg.isCash ? ' (dólar)' : ''}</td>
                                            <td>{money(leg.quoteSpent)}</td>
                                            <td>{qty(leg.quantity)}</td>
                                            <td>{leg.isCash ? '1,00' : price(leg.entryPrice)}</td>
                                            <td>{leg.isCash ? money(leg.valueUsdc) : price(leg.price)}</td>
                                            <td className={tone(leg.pnlUsdc)}>{money(leg.pnlUsdc)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="basket-actions">
                                {confirmSell ? (
                                    <>
                                        <button type="button" className="basket-btn danger" disabled={busy} onClick={onSell}>
                                            {busy ? 'Vendendo...' : 'Confirmar venda e dolarizar'}
                                        </button>
                                        <button type="button" className="basket-btn ghost" disabled={busy} onClick={() => setConfirmSell(false)}>
                                            Cancelar
                                        </button>
                                    </>
                                ) : (
                                    <button type="button" className="basket-btn danger" disabled={busy} onClick={() => setConfirmSell(true)}>
                                        Vender
                                    </button>
                                )}
                                <button type="button" className="basket-btn ghost" onClick={onClose}>Fechar</button>
                            </div>
                        </>
                    ) : (
                        <>
                            {basket.allocation ? (
                                <>
                                    <p className="basket-warn">
                                        Esta compra usa todo o saldo livre ({money(usdcFree)} USDC), dividido entre as moedas.
                                    {basket.key === 'l1' ? ' A parte em USDC permanece em dólar.' : ''}
                                    </p>
                                    <ul className="basket-coins">
                                        {basket.allocation.map(item => (
                                            <li key={item.asset}>
                                                <span>{item.name}{item.isCash ? ' — permanece em dólar' : ''}</span>
                                                <strong>{money(item.quote)} USDC</strong>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <p className="basket-block">{basket.blockReason || 'Esta cesta não está disponível agora.'}</p>
                            )}
                            <div className="basket-actions">
                                <button
                                    type="button"
                                    className="basket-btn primary"
                                    disabled={busy || !basket.canBuy}
                                    onClick={onBuy}
                                >
                                    {busy ? 'Comprando...' : `Investir ${money(usdcFree)} USDC`}
                                </button>
                                <button type="button" className="basket-btn ghost" onClick={onClose}>Fechar</button>
                            </div>
                        </>
                    )}
                    {!position && basket.lastClosed ? (
                        <p className="basket-note">
                            {basket.lastClosed.reason === 'stop'
                                ? `A cesta anterior foi dolarizada no stop de 20% (${money(basket.lastClosed.realizedPnl)} USDC) em ${when(basket.lastClosed.closedAt)}.`
                                : `Última venda em ${when(basket.lastClosed.closedAt)}: ${money(basket.lastClosed.realizedPnl)} USDC.`}
                        </p>
                    ) : null}
                </div>
            ) : null}
        </article>
    );
}

function Baskets() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openKey, setOpenKey] = useState(null);
    const [busyKey, setBusyKey] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });
    const seededRef = useRef(false);

    async function load(background, refresh) {
        if (!background) setLoading(true);
        try {
            const next = await getBaskets(!!refresh);
            setData(next);
            if (!seededRef.current) {
                const opened = (next.baskets || []).find(basket => basket.position);
                if (opened) setOpenKey(opened.key);
                seededRef.current = true;
            }
        } catch (err) {
            if (!background)
                setNotification({ type: 'error', text: apiError(err, 'Não foi possível carregar as cestas.') });
        } finally {
            if (!background) setLoading(false);
        }
    }

    useEffect(() => {
        let alive = true;
        load(false, false);
        const timer = setInterval(() => {
            if (alive) load(true, false);
        }, 25000);
        return () => {
            alive = false;
            clearInterval(timer);
        };
        // A atualização periódica só relê preço e saldo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onBuy(basket) {
        setBusyKey(basket.key);
        setNotification({ type: '', text: '' });
        try {
            const assets = (basket.coins || []).map(coin => coin.asset);
            const next = await buyBasket(basket.key, assets);
            setData(next);
            setOpenKey(basket.key);
            setNotification({ type: 'success', text: next.message || 'Cesta comprada.' });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err, 'Não foi possível comprar a cesta.') });
            load(true, true);
        } finally {
            setBusyKey('');
        }
    }

    async function onSell(basket) {
        setBusyKey(basket.key);
        setNotification({ type: '', text: '' });
        try {
            const next = await sellBasket(basket.key);
            setData(next);
            setNotification({ type: 'success', text: next.message || 'Cesta vendida.' });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err, 'Não foi possível vender a cesta.') });
            load(true, false);
        } finally {
            setBusyKey('');
        }
    }

    const baskets = data?.baskets || [];

    return (
        <>
            <Menu />
            <main className="content">
                <div className="baskets-page">
                    <header className="baskets-header">
                        <div>
                            <h1>Cestas</h1>
                            <p>
                                Quatro cestas prontas. O saldo livre em USDC é dividido entre as moedas,
                                desde que cubra a compra mínima de todas. Se a cesta cair 20%, ela é vendida
                                e o valor volta para dólar.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="baskets-refresh"
                            disabled={loading || !!busyKey}
                            onClick={() => load(false, true)}
                        >
                            Atualizar desempenho
                        </button>
                    </header>

                    <section className="baskets-balance">
                        <div>
                            <span>Saldo livre para cestas</span>
                            <strong>{loading && !data ? '--' : `${money(data?.usdcFree)} USDC`}</strong>
                        </div>
                        <p>
                            {Number(data?.usdcReserved) > 0
                                ? `${money(data.usdcReserved)} USDC já estão em dólar dentro de uma cesta e não entram neste saldo.`
                                : 'A compra de uma cesta usa este saldo inteiro.'}
                        </p>
                    </section>

                    {loading && !data ? <div className="baskets-loading">Calculando o desempenho dos últimos 3 meses...</div> : null}
                    {!loading && data && !baskets.length ? <div className="baskets-empty">Nenhuma cesta disponível agora.</div> : null}

                    <div className="baskets-grid">
                        {baskets.map(basket => (
                            <BasketCard
                                key={basket.key}
                                basket={basket}
                                expanded={openKey === basket.key}
                                busy={busyKey === basket.key}
                                usdcFree={data?.usdcFree}
                                onOpen={() => setOpenKey(basket.key)}
                                onClose={() => setOpenKey(null)}
                                onBuy={() => onBuy(basket)}
                                onSell={() => onSell(basket)}
                            />
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Baskets;
